import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler } from '../PlayerHandler';

interface PlayerRewardProgressProps {
  userId: string;
}

interface ProgressDetail {
  vsGamesWon?: number;
  highestRank?: string;
  consecutiveSingleWins?: number;
  consecutiveVsWins?: number;
  maxDailySinglePlayStreak?: number;
  dailySinglePlayStreak?: number;
  maxDailyVsPlayStreak?: number;
  dailyVsPlayStreak?: number;
  consecutiveSkillWins?: number;
  maxConsecutiveSkillWins?: number;
}

interface MilestoneItem {
  milestoneKey?: string;
  title?: string;
  description?: string;
  category?: string;
  targetValue?: number;
  boards?: number;
  hints?: number;
  adFreeDays?: number;
  isCompleted?: boolean;
}

const PlayerRewardProgress: React.FC<PlayerRewardProgressProps> = ({ userId }) => {
  const { theme, isMobile } = useTheme();
  const [progress, setProgress] = useState<ProgressDetail | null>(null);
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const handler = new PlayerHandler();
        const response = await handler.getPlayerRewardProgress(userId);
        if (response?.success) {
          setProgress(response.progress || null);
          setMilestones(response.milestones || []);
        }
      } catch (err) {
        console.error('Error loading reward progress:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId]);

  const completedCount = milestones.filter(m => m.isCompleted).length;
  const displayMilestones = showAll ? milestones : milestones.slice(0, 10);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      marginTop: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: { fontSize: '16px', fontWeight: 700, color: theme.text, marginBottom: '4px' },
    subtitle: { fontSize: '12px', color: theme.textSecondary, marginBottom: '16px' },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: '8px',
      marginBottom: '16px',
    },
    statBox: {
      padding: '8px',
      borderRadius: '8px',
      background: theme.inputBg || theme.tableHeader,
      textAlign: 'center' as const,
    },
    statLabel: { fontSize: '10px', color: theme.textSecondary, marginBottom: '2px' },
    statValue: { fontSize: '16px', fontWeight: 700, color: theme.text },
    milestoneRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    checkIcon: {
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: 700,
      flexShrink: 0,
    },
    milestoneInfo: { flex: 1 },
    milestoneTitle: { fontSize: '13px', fontWeight: 500, color: theme.text },
    milestoneDesc: { fontSize: '11px', color: theme.textSecondary },
    milestoneReward: { fontSize: '11px', color: '#3b82f6', whiteSpace: 'nowrap' as const },
    showMoreBtn: {
      marginTop: '8px',
      padding: '6px 12px',
      fontSize: '12px',
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'transparent',
      color: theme.textSecondary,
    },
    empty: { fontSize: '13px', color: theme.textSecondary, fontStyle: 'italic' as const },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Reward Progress</div>
        <div style={styles.empty}>Loading...</div>
      </div>
    );
  }

  const progressStats = progress ? [
    { label: 'VS Wins', value: progress.vsGamesWon ?? 0 },
    { label: 'VS Win Streak', value: progress.consecutiveVsWins ?? 0 },
    { label: 'Single Streak', value: progress.consecutiveSingleWins ?? 0 },
    { label: 'Daily VS Streak', value: `${progress.dailyVsPlayStreak ?? 0}/${progress.maxDailyVsPlayStreak ?? 0}` },
    { label: 'Skill Wins', value: `${progress.consecutiveSkillWins ?? 0}/${progress.maxConsecutiveSkillWins ?? 0}` },
  ] : [];

  const getRewardText = (m: MilestoneItem) => {
    const parts: string[] = [];
    if (m.boards) parts.push(`${m.boards}B`);
    if (m.hints) parts.push(`${m.hints}H`);
    if (m.adFreeDays) parts.push(`${m.adFreeDays}d AF`);
    return parts.join(' + ') || '-';
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Reward Progress</div>
      <div style={styles.subtitle}>
        {completedCount}/{milestones.length} milestones completed
      </div>

      {progressStats.length > 0 && (
        <div style={styles.statsGrid}>
          {progressStats.map(s => (
            <div key={s.label} style={styles.statBox}>
              <div style={styles.statLabel}>{s.label}</div>
              <div style={styles.statValue}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {displayMilestones.map((m, i) => (
        <div key={i} style={styles.milestoneRow}>
          <div style={{
            ...styles.checkIcon,
            background: m.isCompleted ? 'rgba(34, 197, 94, 0.15)' : theme.tableHeader,
            color: m.isCompleted ? '#22c55e' : theme.textSecondary,
          }}>
            {m.isCompleted ? '✓' : '○'}
          </div>
          <div style={styles.milestoneInfo}>
            <div style={styles.milestoneTitle}>{m.title || m.milestoneKey}</div>
            <div style={styles.milestoneDesc}>{m.description}</div>
          </div>
          <div style={styles.milestoneReward}>{getRewardText(m)}</div>
        </div>
      ))}

      {milestones.length > 10 && (
        <button style={styles.showMoreBtn} onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show Less' : `Show All (${milestones.length})`}
        </button>
      )}
    </div>
  );
};

export default PlayerRewardProgress;
